
import React from 'react';

export const MarketAnalysisTerms: React.FC = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Market Analysis and Valuation Terms
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Understanding market analysis terminology helps you evaluate your property's position in the market and 
            make informed decisions about pricing, timing, and positioning your apartment building for sale.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Comparative Market Analysis (CMA)</h3>
              
              <div className="space-y-6">
                <div className="bg-teal-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-teal-800 mb-3">What CMA Includes</h4>
                  <p className="text-gray-700 mb-4">
                    A comprehensive analysis of similar properties that have sold, are currently listed, or are under 
                    contract in your market area. Essential for accurate pricing.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm"><strong>Sold Comparables:</strong> Recent sales within 6-12 months</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm"><strong>Active Listings:</strong> Current market competition</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm"><strong>Pending Sales:</strong> Properties under contract</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-teal-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm"><strong>Market Trends:</strong> Price and activity patterns</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-800 mb-3">Adjustment Factors</h4>
                  <p className="text-gray-700 mb-3">
                    Comparable properties are adjusted for differences to determine your property's market value.
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-white p-3 rounded-lg">
                      <div className="font-medium text-gray-900">Size & Units</div>
                      <div className="text-gray-600">±$20K-50K per unit difference</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <div className="font-medium text-gray-900">Condition</div>
                      <div className="text-gray-600">±5-25% value adjustment</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <div className="font-medium text-gray-900">Location</div>
                      <div className="text-gray-600">±10-30% value difference</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <div className="font-medium text-gray-900">Age</div>
                      <div className="text-gray-600">±2-8% per 10 years</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/64ba165738c15026ff8006ef60dbc8fe.jpg"
              alt="Market analysis charts and comparative property data visualization"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Key Market Indicators</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-trending-up-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Appreciation Rate</h4>
              <p className="text-gray-600 text-sm mb-3">
                The annual percentage increase in property values over time. Historical appreciation helps predict 
                future value trends and optimal selling times.
              </p>
              <div className="text-xs text-green-600 font-medium">
                Average: 3-6% annually for apartments
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Days on Market (DOM)</h4>
              <p className="text-gray-600 text-sm mb-3">
                Average time properties stay listed before selling. Lower DOM indicates strong demand and 
                seller-favorable conditions in the market.
              </p>
              <div className="text-xs text-blue-600 font-medium">
                Commercial: 90-180 days typical
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-inbox-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Inventory Levels</h4>
              <p className="text-gray-600 text-sm mb-3">
                Number of properties available for sale. Low inventory typically supports higher prices, while 
                high inventory may pressure prices downward.
              </p>
              <div className="text-xs text-purple-600 font-medium">
                Impact: Supply and demand balance
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-funds-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Price Per Unit</h4>
              <p className="text-gray-600 text-sm mb-3">
                Sale price divided by number of units. Useful for quick comparisons between properties of 
                different sizes in the same market area.
              </p>
              <div className="text-xs text-red-600 font-medium">
                Formula: Sale Price ÷ Number of Units
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-2xl text-yellow-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Price Per Square Foot</h4>
              <p className="text-gray-600 text-sm mb-3">
                Sale price divided by total building square footage. Helps compare properties with different 
                unit sizes and configurations in market analysis.
              </p>
              <div className="text-xs text-yellow-600 font-medium">
                Formula: Sale Price ÷ Total Sq Ft
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-arrow-up-down-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Absorption Rate</h4>
              <p className="text-gray-600 text-sm mb-3">
                Rate at which properties sell in a given market. Calculated by dividing monthly sales by 
                total inventory to determine months of supply.
              </p>
              <div className="text-xs text-teal-600 font-medium">
                Formula: Monthly Sales ÷ Total Inventory
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=real%20estate%20market%20trends%20analysis%20with%20economic%20indicators%2C%20apartment%20building%20market%20cycle%20visualization%2C%20supply%20and%20demand%20charts%20for%20commercial%20property%20investment&width=600&height=400&seq=market-trends-analysis&orientation=landscape"
              alt="Real estate market trends and economic indicators analysis"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>

          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Market Cycle Phases</h3>
              
              <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-800 mb-3">Expansion Phase</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <i className="ri-arrow-up-line text-green-600 mr-2"></i>
                      <span className="text-gray-700">Rising property values and rents</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-up-line text-green-600 mr-2"></i>
                      <span className="text-gray-700">Increasing construction activity</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-up-line text-green-600 mr-2"></i>
                      <span className="text-gray-700">High investor confidence</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-800 mb-3">Peak Phase</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <i className="ri-stop-line text-blue-600 mr-2"></i>
                      <span className="text-gray-700">Price growth slows or stops</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-stop-line text-blue-600 mr-2"></i>
                      <span className="text-gray-700">Construction reaches maximum</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-stop-line text-blue-600 mr-2"></i>
                      <span className="text-gray-700">Market becomes saturated</span>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-red-800 mb-3">Contraction Phase</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <i className="ri-arrow-down-line text-red-600 mr-2"></i>
                      <span className="text-gray-700">Declining property values</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-down-line text-red-600 mr-2"></i>
                      <span className="text-gray-700">Reduced construction activity</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-arrow-down-line text-red-600 mr-2"></i>
                      <span className="text-gray-700">Increased vacancy rates</span>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-yellow-800 mb-3">Recovery Phase</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <i className="ri-refresh-line text-yellow-600 mr-2"></i>
                      <span className="text-gray-700">Market stabilization begins</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-refresh-line text-yellow-600 mr-2"></i>
                      <span className="text-gray-700">Cautious investor re-entry</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-refresh-line text-yellow-600 mr-2"></i>
                      <span className="text-gray-700">Foundation for next expansion</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8 rounded-2xl max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Market Timing Can Make or Break Your Sale</h3>
            <p className="text-lg opacity-90 leading-relaxed mb-4">
              Understanding market analysis terms and indicators helps you identify optimal selling opportunities. 
              The difference between selling at peak market conditions versus a downturn can be 15-25% of your 
              property's value.
            </p>
            <div className="text-sm opacity-75">
              Stay informed about local market conditions through regular CMA updates, economic indicators, 
              and professional market reports from experienced commercial real estate professionals.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
